import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Shield, Lock, Eye, Cookie, FileText, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="mx-auto mb-8 p-4">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-white">Privacy & Security</h1>
        <p className="text-lg text-gray-300">Your data protection and privacy are our top priorities</p>
      </div>

      <div className="grid gap-6">
        <Card className="border-gray-700 bg-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Shield className="h-8 w-8 text-green-400" />
              Data Protection at Mithrl
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 text-gray-300">
            <p className="text-sm">
              At Mithrl, we understand the sensitive nature of genomic and research data. Our platform is built with
              enterprise-grade security measures and strict privacy controls to ensure your data remains secure and
              confidential throughout the analysis process.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-green-900 text-[1.2rem] text-green-200">SOC 2 In Progress</Badge>
              <Badge className="bg-purple-900 text-[1.2rem] text-purple-200">End-to-End Encryption</Badge>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-6 xs:grid-cols-1">
          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Lock className="h-8 w-8 text-blue-400" />
                Security Features
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">256-bit AES Encryption</h4>
                    <p className="text-sm text-gray-400">All data encrypted in transit and at rest</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">Regular Security Audits</h4>
                    <p className="text-sm text-gray-400">Third-party penetration testing and compliance checks</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">Secure Cloud Infrastructure</h4>
                    <p className="text-sm text-gray-400">Hosted on certified cloud providers</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Eye className="h-8 w-8 text-purple-400" />
                Privacy Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">Data Minimization</h4>
                    <p className="text-sm text-gray-400">We only collect data necessary for analysis</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">Access Controls</h4>
                    <p className="text-sm text-gray-400">Permissions and role-based access</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-8 w-8 flex-shrink-0 text-green-400" />
                  <div>
                    <h4 className="text-sm font-medium text-white">Data Portability</h4>
                    <p className="text-sm text-gray-400">Export your data anytime in standard formats</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-blue-700 bg-gradient-to-r from-blue-900 to-purple-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <FileText className="h-8 w-8 text-cyan-400" />
              Legal Documents & Policies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-blue-200">
              Review our comprehensive privacy and cookie policies to understand how we protect and handle your data.
            </p>

            <div className="grid grid-cols-2 gap-4 xs:grid-cols-1">
              <Link
                to="/privacy"
                className="group cursor-pointer rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-sm transition-colors hover:border-white/40"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-blue-500 p-2">
                      <Shield className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">Privacy Policy</h3>
                      <p className="text-sm text-blue-200">How we collect, use, and protect your data</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/cookie-policy"
                className="group cursor-pointer rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-sm transition-colors hover:border-white/40"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-orange-500 p-2">
                      <Cookie className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">Cookie Policy</h3>
                      <p className="text-sm text-blue-200">Information about cookies and tracking</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
